// composables/useApi.ts
import { asOfetchError, type ApiResponse } from "~/types/apiResponse";
import { useServiceRegistry } from "./useServiceRegistry";
import type { FetchOptions } from "ofetch";
import { useDeviceId } from "./useDevice";
import { apiRoutes } from "~/routes";

export type TypedFetchOptions = Omit<FetchOptions, "method"> & {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "HEAD" | "OPTIONS";
};

export type RequestBody = BodyInit | Record<string, unknown> | null | undefined;

let isRefreshing = false;
let refreshPromise: Promise<unknown> | null = null;

const createServiceClient = async (getServiceUrl: () => string) => {
  const baseHeaders = { "Content-Type": "application/json" };
  const baseFetchOptions = {
    credentials: "include",
    cache: "no-store",
  } as const;

  const getHeaders = () => {
    const authHeaders = isClientSide()
      ? (() => {
          const accessToken = useAuth()?.accessToken?.value;
          return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
        })()
      : {};

    const deviceHeaders = isClientSide()
      ? (() => {
          const device = useDeviceId().deviceInfo?.value;
          return device
            ? {
                "X-Client-Platform": device.platform,
                "X-Client-Device-Id": device.deviceId,
                "X-Client-fingerprint": device.fingerprint || "",
              }
            : {};
        })()
      : {};

    return {
      ...baseHeaders,
      ...deviceHeaders,
      ...authHeaders,
    };
  };

  const detectErrorType = (errorMessage: string, errorName?: string) => {
    const msg = errorMessage.toLowerCase();

    const corsKeywords = [
      "cors",
      "cross-origin",
      "access-control-allow-origin",
      "preflight",
      "blocked by cors policy",
    ];
    if (
      corsKeywords.some((k) => msg.includes(k)) ||
      errorName === "TypeError"
    ) {
      return "CORS_ERROR";
    }

    const networkKeywords = [
      "failed to fetch",
      "networkerror",
      "network request failed",
      "connection refused",
      "connection timed out",
      "net::err_",
      "fetch failed",
    ];
    if (networkKeywords.some((k) => msg.includes(k))) {
      return "NETWORK_ERROR";
    }

    if (msg.includes("timeout") || msg.includes("timed out")) {
      return "TIMEOUT_ERROR";
    }

    return null;
  };

  const getErrorMessage = (errorType: string): string => {
    const messages = {
      CORS_ERROR: "Cannot connect to server due to security restrictions.",
      NETWORK_ERROR: "Network connection failed. Please check your internet.",
      TIMEOUT_ERROR: "Request timed out. Server is taking too long to respond.",
      DEFAULT: "An error occurred while processing your request.",
    };
    return messages[errorType as keyof typeof messages] || messages.DEFAULT;
  };

  const refreshAccessToken = async () => {
    if (isRefreshing && refreshPromise) {
      return refreshPromise;
    }

    isRefreshing = true;

    try {
      refreshPromise = (async () => {
        const response = await useRefreshToken();

        if (response?.status === "success" && response?.data) {
          const { setAccessToken, setUser } = useAuth();
          setAccessToken(response.data.accessToken);
          setUser(response.data.user);
          return true;
        }

        throw new Error("Refresh failed");
      })();

      return await refreshPromise;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  };

  const fetchWithStatusCode = async <T>(
    fetchPromise: Promise<ApiResponse<T>>,
    retryFn?: () => Promise<ApiResponse<T>>,
    endpoint?: string,
  ): Promise<ApiResponse<T>> => {
    try {
      const response = await fetchPromise;
      return { ...response, statusCode: response.statusCode || 200 };
    } catch (error: unknown) {
      const ofetchError = asOfetchError(error);

      if (!ofetchError) {
        return {
          status: "failure",
          code: "UNKNOWN_ERROR",
          data: null,
          error: "An unexpected error occurred",
          statusCode: 0,
        };
      }

      const errorMessage = ofetchError.message || "";
      const statusCode = ofetchError.response?.status || 0;

      if (
        statusCode === 401 &&
        retryFn &&
        endpoint !== apiRoutes.refreshToken.path
      ) {
        try {
          await refreshAccessToken();
          return await retryFn();
        } catch (refreshError: unknown) {
          console.error(refreshError);

          const { clear } = useAuth();
          clear();

          return {
            status: "failure",
            code: "UNAUTHORIZED",
            data: null,
            error: "Session expired. Please login again.",
            statusCode: 401,
          };
        }
      }

      if (statusCode === 0) {
        const errorType = detectErrorType(errorMessage, ofetchError.name);
        if (errorType) {
          return {
            status: "failure",
            code: errorType,
            data: null,
            error: getErrorMessage(errorType),
            statusCode: 0,
          };
        }
      }

      const responseData = ofetchError.response?._data;
      if (
        responseData &&
        typeof responseData === "object" &&
        "status" in responseData &&
        "code" in responseData
      ) {
        return { ...(responseData as ApiResponse<T>), statusCode };
      }

      return {
        status: "failure",
        code: statusCode ? `HTTP_${statusCode}` : "CONNECTION_ERROR",
        data: null,
        error: errorMessage || "Request failed",
        statusCode,
      };
    }
  };

  const get = async <T>(
    endpoint: string,
    options: TypedFetchOptions = {},
  ): Promise<ApiResponse<T>> => {
    const url = `${getServiceUrl()}${endpoint}`;

    const makeRequest = () =>
      $fetch<ApiResponse<T>>(url, {
        method: "GET",
        headers: getHeaders() as Record<string, string>,
        ...baseFetchOptions,
        ...options,
      });

    return fetchWithStatusCode<T>(makeRequest(), makeRequest, endpoint);
  };

  const post = async <T>(
    endpoint: string,
    body?: RequestBody,
    options: TypedFetchOptions = {},
  ): Promise<ApiResponse<T>> => {
    const url = `${getServiceUrl()}${endpoint}`;
    const makeRequest = () =>
      $fetch<ApiResponse<T>>(url, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
        headers: getHeaders() as Record<string, string>,
        ...baseFetchOptions,
        ...options,
      });

    return fetchWithStatusCode<T>(makeRequest(), makeRequest, endpoint);
  };

  const put = async <T>(
    endpoint: string,
    body?: RequestBody,
    options: TypedFetchOptions = {},
  ): Promise<ApiResponse<T>> => {
    const url = `${getServiceUrl()}${endpoint}`;
    const makeRequest = () =>
      $fetch<ApiResponse<T>>(url, {
        method: "PUT",
        body: body ? JSON.stringify(body) : undefined,
        headers: getHeaders() as Record<string, string>,
        ...baseFetchOptions,
        ...options,
      });

    return fetchWithStatusCode<T>(makeRequest(), makeRequest, endpoint);
  };

  const patch = async <T>(
    endpoint: string,
    body?: RequestBody,
    options: TypedFetchOptions = {},
  ): Promise<ApiResponse<T>> => {
    const url = `${getServiceUrl()}${endpoint}`;
    const makeRequest = () =>
      $fetch<ApiResponse<T>>(url, {
        method: "PATCH",
        body: body ? JSON.stringify(body) : undefined,
        headers: getHeaders() as Record<string, string>,
        ...baseFetchOptions,
        ...options,
      });

    return fetchWithStatusCode<T>(makeRequest(), makeRequest, endpoint);
  };

  const del = async <T>(
    endpoint: string,
    options: TypedFetchOptions = {},
  ): Promise<ApiResponse<T>> => {
    const url = `${getServiceUrl()}${endpoint}`;
    const makeRequest = () =>
      $fetch<ApiResponse<T>>(url, {
        method: "DELETE",
        headers: getHeaders() as Record<string, string>,
        ...baseFetchOptions,
        ...options,
      });

    return fetchWithStatusCode<T>(makeRequest(), makeRequest, endpoint);
  };

  return { get, post, put, patch, delete: del };
};

export const useApi = async () => {
  const { getAuthServiceUrl, getTranslatorServiceUrl, getAuthUserServiceUrl } =
    useServiceRegistry();
  return {
    auth: await createServiceClient(getAuthServiceUrl),
    users: await createServiceClient(getAuthUserServiceUrl),
    translator: await createServiceClient(getTranslatorServiceUrl),
  };
};
